import { useState, useEffect } from 'react';
import { CheckCircle2 } from 'lucide-react';
import { usaMarcaDaLoja, RECUSA_MARCA_NOME, mascaraTelefoneBR } from '@fiodecobre/shared';
import { appApi, setTokenApp, ErroApp, getTelefoneLembrado, setTelefoneLembrado, type ClienteApp, type ConviteApp } from './api';
import { CampoSenha, estiloCampo } from './campos';
import { EsqueciSenhaApp } from './EsqueciSenhaApp';

// Tela principal do aplicativo do cliente: entrar — e criar conta SO com convite.
//
// ⛔ **Cadastro livre nao existe mais** (16/08/2026, decisao do Ruan). A aba
// "Criar conta" so aparece pra quem chegou por um link de 1o acesso; sem link, a
// tela mostra so a entrada e diz como conseguir acesso. Motivo: o formulario
// antigo amarrava a ficha do cliente pelo TELEFONE DIGITADO, entao qualquer um
// que soubesse o numero de uma empresa (Google, Instagram, lateral da
// caminhonete) nascia com credencial valida apontando pra ficha dela, escolhendo
// a propria senha. Com titulo em aberto e nota fiscal atras do login, isso seria
// o financeiro do cliente. Ver o comentario do `/cadastro` no backend.
//
// ⚠️ O telefone e' a identidade — nao ha "usuario" nem e-mail obrigatorio. No
// cadastro ele vem do convite; na entrada, e' o que a pessoa digita.

interface Props {
  aoEntrar: (cliente: ClienteApp) => void;
  /** Veio de um link de 1o acesso: a loja ja sabe quem e'. */
  convite?: (ConviteApp & { token: string }) | null;
}

const COBRE = '#b45309';

/**
 * ⚠️ **A mascara e o "tira o DDI" viraram peca unica do `shared`** (30/08/2026) —
 * aqui viviam duas copias caseiras, e a do cadastro de cliente tinha ficado sem a
 * metade que protege: numero COLADO (autofill do iPhone, copiar do WhatsApp)
 * gravava errado. O `mascaraTelefoneBR` ja chega com as duas metades.
 */
export function EntradaApp({ aoEntrar, convite }: Props) {
  // A porta da EQUIPE. Mesmo aplicativo, mesmo icone: quem entra por aqui ve a lista
  // de todos os clientes em vez da propria conversa com a loja.
  const [aba, setAba] = useState<'entrar' | 'criar'>('entrar');
  const [nome, setNome] = useState('');
  // Ja vem preenchido com o ultimo numero que entrou neste aparelho. Quem abre o
  // app de novo digita so a senha — e no iPhone nem isso, se ele deixou o cofre
  // do aparelho guardar.
  const [telefone, setTelefone] = useState(getTelefoneLembrado);
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [senhaConfirma, setSenhaConfirma] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  /** "Esqueci minha senha" toma a tela inteira: sao dois caminhos diferentes, e
   *  misturar os campos dos dois faria o formulario dizer duas coisas. */
  const [esqueci, setEsqueci] = useState(false);

  // O convite chega depois do 1o render (a tela pergunta ao servidor o que o token
  // vale). Quando ele chega, a tela vira a de cadastro com o nome ja escrito — mas
  // so sobrescreve o nome se a pessoa ainda nao digitou nada, pra nao apagar o que
  // ela acabou de corrigir.
  useEffect(() => {
    if (!convite) return;
    setAba('criar');
    setNome((atual) => (atual.trim() ? atual : (convite.nome ?? '')));
  }, [convite]);

  function trocarAba(nova: 'entrar' | 'criar') {
    setAba(nova);
    setErro(null);
    // A confirmacao nao sobrevive a troca de aba: ela so existe no cadastro, e
    // deixar valor velho ali faria o botao liberar ou travar por um campo que a
    // pessoa nem esta vendo.
    setSenhaConfirma('');
  }

  async function submeter(e: React.FormEvent) {
    e.preventDefault();
    setErro(null);
    setEnviando(true);
    try {
      // O telefone nunca e' enviado no cadastro: quem manda e' o token do convite.
      // E' o que torna o numero provado — se a tela mandasse o campo, editar o
      // campo furaria a prova. E sem convite nao ha aba de cadastro, entao este
      // ramo so roda com `convite` preenchido.
      const sessao = aba === 'criar' && convite
        ? await appApi.cadastrar({ nome, senha, email: email || undefined, convite: convite.token })
        : await appApi.entrar({ telefone, senha });
      setTokenApp(sessao.token);
      // Guarda o numero pra proxima abertura. Sai do que o SERVIDOR devolveu, nao
      // do campo: no cadastro por convite o campo nem existe (o telefone vem do
      // token), e ali tambem tem que ser lembrado.
      setTelefoneLembrado(mascaraTelefoneBR(sessao.cliente.telefone));
      aoEntrar(sessao.cliente);
    } catch (err) {
      const e2 = err as ErroApp;
      setErro(e2.message);
      // Numero ja cadastrado: em vez de so avisar, leva pra aba certa com o
      // telefone preservado — o proximo passo dele e' entrar, nao reescrever.
      if (e2.corpo?.ja_cadastrado) setAba('entrar');
      // ⛔ A loja liberou senha nova pra este numero: a tela LEVA pra la, nao so
      // avisa. Quem digitou a senha que queria criar (02/09/2026 — foi o que o
      // Ruan fez testando) leria o aviso e teria que achar o botao sozinho; o
      // caminho ja esta decidido, e a tela sabe qual e'.
      if (e2.corpo?.motivo === 'redefinir_liberado') setEsqueci(true);
    } finally {
      setEnviando(false);
    }
  }

  const senhasBatem = senha === senhaConfirma;
  const telefoneOk = !!convite || telefone.replace(/\D/g, '').length >= 10;
  // ⛔ Este campo e' a ASSINATURA DO PAPEL, nao so o apelido de quem entra: enquanto a
  // Fantasia estiver vazia — e toda conta nasce assim — e' ele que o PDF do orcamento
  // imprime no topo. Quem recusa e' o `POST /cadastro`; aqui e' so o aviso, pra pessoa
  // nao levar um 400 depois de preencher a senha inteira ([marca-loja.ts]).
  const nomeDaLoja = aba === 'criar' && usaMarcaDaLoja(nome);
  const podeEnviar = aba === 'criar'
    ? nome.trim().length >= 2 && !nomeDaLoja && telefoneOk && senha.length >= 6 && senhasBatem && senhaConfirma.length > 0
    : telefone.replace(/\D/g, '').length >= 10 && senha.length >= 1;

  // ⚠️ Botao cinza sem dizer o porque e' beco sem saida: a pessoa digita, nada
  // acontece e ela nao tem como adivinhar o que falta (o placeholder some no
  // primeiro caractere). Este aviso nasceu de ver o Ruan parado numa senha de 4
  // caracteres com o botao apagado e nenhuma explicacao na tela.
  const aviso = aba !== 'criar' ? null
    // Primeiro da fila de proposito: e' o campo do topo, e e' a recusa mais
    // surpreendente das tres — as outras duas a pessoa ja espera.
    : nomeDaLoja ? RECUSA_MARCA_NOME
    : senha.length > 0 && senha.length < 6 ? 'A senha precisa de pelo menos 6 caracteres.'
    : senhaConfirma.length > 0 && !senhasBatem ? 'As duas senhas estao diferentes.'
    : null;

  return (
    <div className="app-cliente-root" style={{ minHeight: '100dvh', backgroundColor: '#f7f7f7', color: '#111' }}>
      <header style={{ backgroundColor: COBRE, color: '#fff', padding: '28px 20px 32px' }}>
        <h1 style={{ fontSize: 26, fontWeight: 800, letterSpacing: 0.5 }}>FIO DE COBRE</h1>
        {/* ⚠️ A promessa e' sobre o CANAL, nunca sobre o tempo de resposta. Escrever
            "a qualquer hora" faz o cliente ler "sempre tem alguem" — e se as tres
            estiverem no balcao a mensagem espera igual. Promessa quebrada aqui
            queima mais rapido que no WhatsApp, porque a marca na tela e' a da loja. */}
        <p style={{ marginTop: 6, fontSize: 15, opacity: 0.92 }}>
          Sua conversa com a loja, num lugar só.
        </p>
      </header>

      <main style={{ maxWidth: 460, margin: '0 auto', padding: '20px 16px 40px' }}>
        {esqueci ? (
          <EsqueciSenhaApp
            telefoneInicial={telefone}
            // ⚠️ A tela trocou sozinha debaixo do dedo de quem tentou entrar — sem
            // esta linha, o motivo da troca fica so na mensagem que acabou de sumir
            // junto com o formulario anterior.
            avisoInicial={erro && esqueci ? erro : null}
            aoEntrar={aoEntrar}
            aoVoltar={() => { setEsqueci(false); setErro(null); }}
          />
        ) : (
        <>
        {/* As abas so existem quando ha convite. Sem ele nao ha o que escolher —
            e mostrar uma aba "Criar conta" que o servidor recusa seria o beco sem
            saida que esta tela ja aprendeu a nao fazer. */}
        {convite && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
          {(['entrar', 'criar'] as const).map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => trocarAba(v)}
              style={{
                flex: 1,
                height: 46,
                borderRadius: 10,
                fontSize: 15,
                fontWeight: 700,
                border: aba === v ? `2px solid ${COBRE}` : '1px solid #d1d5db',
                backgroundColor: aba === v ? '#fff8ef' : '#fff',
                color: aba === v ? COBRE : '#444',
              }}
            >
              {v === 'entrar' ? 'Entrar' : 'Criar conta'}
            </button>
          ))}
        </div>
        )}

        <form onSubmit={submeter} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {aba === 'criar' && (
            <label style={{ display: 'block' }}>
              <span style={{ fontSize: 14, fontWeight: 600 }}>Seu nome</span>
              <input
                className="no-upper"
                style={{ ...estiloCampo, marginTop: 6 }}
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                autoComplete="name"
                placeholder="Como a loja te chama"
              />
            </label>
          )}

          {convite && aba === 'criar' ? (
            // Numero que veio do link: NAO e' campo. Deixa-lo editavel daria a
            // impressao de que dá pra trocar — e trocar nao mudaria nada, porque o
            // cadastro usa o telefone do convite e ignora o da tela. Campo que
            // aceita digitacao e nao tem efeito e' pior que campo nenhum.
            // Vai mascarado porque o link pode ter sido encaminhado.
            <div>
              <span style={{ fontSize: 14, fontWeight: 600 }}>Seu celular</span>
              {/* ⚠️ Nada de verde pra dizer "confirmado": o Ruan nao distingue
                  verde de vermelho, e o padrao da casa e' nunca deixar a cor ser
                  a portadora do sinal. Aqui o significado vem do icone + do texto,
                  e a cor e' o cobre da marca — que ainda diz melhor o que precisa
                  ser dito: "este dado veio da loja". */}
              <div style={{
                marginTop: 6, height: 52, padding: '0 14px', borderRadius: 10,
                backgroundColor: '#fff8ef', border: `1px solid ${COBRE}`,
                display: 'flex', alignItems: 'center', gap: 10, fontSize: 16,
              }}>
                <CheckCircle2 size={20} color={COBRE} />
                <span style={{ fontVariantNumeric: 'tabular-nums' }}>{convite.telefone_mascarado}</span>
              </div>
              <span style={{ fontSize: 13, color: COBRE, display: 'block', marginTop: 5 }}>
                Confirmado pela loja — você não precisa digitar.
              </span>
            </div>
          ) : (
            <label style={{ display: 'block' }}>
              <span style={{ fontSize: 14, fontWeight: 600 }}>Celular com DDD</span>
              <input
                className="no-upper"
                style={{ ...estiloCampo, marginTop: 6 }}
                value={telefone}
                onChange={(e) => setTelefone(mascaraTelefoneBR(e.target.value))}
                inputMode="numeric"
                autoComplete="tel"
                placeholder="(64) 90000-0000"
              />
            </label>
          )}

          {aba === 'criar' && (
            <label style={{ display: 'block' }}>
              <span style={{ fontSize: 14, fontWeight: 600 }}>
                E-mail <span style={{ fontWeight: 400, color: '#6b7280' }}>(opcional)</span>
              </span>
              <input
                className="no-upper"
                style={{ ...estiloCampo, marginTop: 6 }}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                type="email"
                autoComplete="email"
              />
            </label>
          )}

          <CampoSenha
            rotulo="Senha"
            valor={senha}
            aoMudar={setSenha}
            autoComplete={aba === 'criar' ? 'new-password' : 'current-password'}
            placeholder={aba === 'criar' ? 'Pelo menos 6 caracteres' : undefined}
          />

          {aba === 'criar' && (
            <CampoSenha
              rotulo="Confirmar senha"
              valor={senhaConfirma}
              aoMudar={setSenhaConfirma}
              autoComplete="new-password"
              placeholder="Repita a senha"
            />
          )}

          {aviso && (
            <div style={{ backgroundColor: '#fffbeb', color: '#92400e', padding: '10px 12px', borderRadius: 8, fontSize: 14 }}>
              {aviso}
            </div>
          )}

          {erro && (
            <div style={{ backgroundColor: '#fef2f2', color: '#b91c1c', padding: '10px 12px', borderRadius: 8, fontSize: 14 }}>
              {erro}
            </div>
          )}

          <button
            type="submit"
            disabled={!podeEnviar || enviando}
            style={{
              height: 52,
              borderRadius: 10,
              backgroundColor: podeEnviar && !enviando ? COBRE : '#d6d3d1',
              color: '#fff',
              fontSize: 16,
              fontWeight: 700,
              marginTop: 4,
            }}
          >
            {enviando ? 'Aguarde...' : aba === 'criar' ? 'Criar minha conta' : 'Entrar'}
          </button>
        </form>

        {/* ⚠️ So na aba de ENTRAR: quem esta criando a conta por convite ainda nao
            tem senha pra esquecer, e a porta ali confundiria. Fica logo abaixo do
            botao porque e' onde a pessoa esta olhando quando descobre que nao
            lembra — e ate hoje nao havia NADA neste ponto da tela. */}
        {aba === 'entrar' && (
          <button
            type="button"
            onClick={() => { setEsqueci(true); setErro(null); }}
            style={{
              marginTop: 14, width: '100%', height: 44,
              color: COBRE, fontSize: 14.5, fontWeight: 700, background: 'transparent',
            }}
          >
            Esqueci minha senha
          </button>
        )}

        <p style={{ marginTop: 22, fontSize: 13, color: '#6b7280', lineHeight: 1.5 }}>
          Sua conversa fica guardada aqui e a loja responde por esta mesma tela.
        </p>

        {/* ⚠️ Quem chega sem convite precisa sair daqui sabendo O QUE FAZER. Uma
            tela que so oferece "Entrar" a quem nunca entrou e' uma porta trancada
            sem campainha — a pessoa conclui que o app nao e' pra ela e fecha.
            Mesma regra do botao desabilitado que diz o motivo. */}
        {!convite && (
          <div style={{
            marginTop: 18, padding: '14px 14px', borderRadius: 10,
            backgroundColor: '#fff8ef', border: '1px solid #f0d5b0',
          }}>
            <p style={{ fontSize: 14, fontWeight: 700, color: COBRE }}>Ainda não tem acesso?</p>
            <p style={{ marginTop: 6, fontSize: 13, color: '#5b4636', lineHeight: 1.5 }}>
              O aplicativo é liberado por convite da loja, para o seu celular ficar
              confirmado. Fale com a gente pelo WhatsApp e peça o seu link de acesso.
            </p>
          </div>
        )}

        {/* ⛔ AQUI havia um botao "Sou da loja", removido em 14/08/2026 por decisao do
            Ruan, na onda que criou o papel de administrador.

            Ele ficava nesta tela, que e' PUBLICA — qualquer pessoa que abrisse `/app`,
            sem conta e sem convite, via a porta da equipe e um formulario de senha do
            sistema. A porta agora existe so dentro do menu ☰, e so pra quem tem
            `papel = 'administrador'`.

            ⛔ Nao repor "pra facilitar o acesso da loja": o caminho de quem e' da
            equipe e' entrar com a conta de cliente e usar o menu. E se um dia ela
            voltar, volta com a licao que a criou — discreto NAO e' apagado: a 1a
            versao era um texto cinza-claro de 13px e o Ruan respondeu *"Nao vi onde
            acessar"*. */}
        </>
        )}
      </main>
    </div>
  );
}
